class ClcPdpMobileTabs extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.lockedScrollY = 0;
    this.youtubeApiPromise = null;
    this.selectors = {
      tab: "[data-clc-tabs-tab]",
      panel: "[data-clc-tabs-panel]",
      modal: "[data-clc-tabs-modal]",
      frame: "[data-clc-tabs-frame]",
      modalTrigger: "[data-clc-tabs-modal-trigger]",
      inlineVideo: "[data-clc-inline-video]",
    };

    this.bindTabs();
    this.bindModalTriggers();
    this.bindModal();
    this.bindInlineVideos();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.unlockPageScroll();
  }

  bindTabs() {
    const tabs = this.querySelectorAll(this.selectors.tab);
    const panels = this.querySelectorAll(this.selectors.panel);

    tabs.forEach((tab) => {
      tab.addEventListener(
        "click",
        () => {
          const target = tab.dataset.clcTabsTab;
          const activePanel = this.querySelector(`${this.selectors.panel}.is-active`);

          if (activePanel?.dataset.clcTabsPanel === "how_to" && target !== "how_to") {
            this.pauseInlineVideos();
          }

          tabs.forEach((item) => {
            const isActive = item.dataset.clcTabsTab === target;
            item.classList.toggle("is-active", isActive);
            item.setAttribute("aria-selected", isActive ? "true" : "false");
          });

          panels.forEach((panel) => {
            const isActive = panel.dataset.clcTabsPanel === target;
            panel.classList.toggle("is-active", isActive);
            panel.hidden = !isActive;
          });

          tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        },
        { signal: this.abortController.signal },
      );
    });
  }

  bindModalTriggers() {
    this.querySelectorAll(this.selectors.modalTrigger).forEach((trigger) => {
      trigger.addEventListener(
        "click",
        () => {
          const modal = this.querySelector(this.selectors.modal);
          const frame = this.querySelector(this.selectors.frame);
          const embedUrl = this.getYoutubeEmbedUrl(trigger.dataset.clcTabsVideoUrl);

          if (!modal || !frame || !embedUrl) {
            return;
          }

          frame.src = embedUrl;

          if (typeof modal.showModal === "function") {
            modal.showModal();
          } else {
            modal.setAttribute("open", "");
          }

          this.lockPageScroll();
        },
        { signal: this.abortController.signal },
      );
    });
  }

  bindInlineVideos() {
    this.querySelectorAll(this.selectors.inlineVideo).forEach((container) => {
      const type = container.dataset.clcInlineVideoType;

      if (type === "native") {
        this.bindNativeInlineVideo(container);
        return;
      }

      if (type === "youtube") {
        this.bindYoutubeInlineVideo(container);
      }
    });
  }

  bindNativeInlineVideo(container) {
    const video = container.querySelector("video");
    const overlay = container.querySelector("[data-clc-inline-video-overlay]");

    if (!video || !overlay) {
      return;
    }

    const showOverlay = () => overlay.classList.remove("is-hidden");
    const hideOverlay = () => overlay.classList.add("is-hidden");

    container.pauseInlineVideo = () => {
      video.pause();
      video.currentTime = 0;
      showOverlay();
    };

    const playVideo = () => {
      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          showOverlay();
        });
      }
    };

    overlay.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        playVideo();
      },
      { signal: this.abortController.signal },
    );

    video.addEventListener(
      "click",
      () => {
        if (video.paused) {
          playVideo();
          return;
        }

        video.pause();
      },
      { signal: this.abortController.signal },
    );

    video.addEventListener("play", hideOverlay, { signal: this.abortController.signal });
    video.addEventListener("pause", showOverlay, { signal: this.abortController.signal });
    video.addEventListener("ended", showOverlay, { signal: this.abortController.signal });
  }

  bindYoutubeInlineVideo(container) {
    const overlay = container.querySelector("[data-clc-inline-video-overlay]");
    const playerHost = container.querySelector("[data-clc-inline-video-player]");
    const videoId = this.getYoutubeVideoId(container.dataset.clcInlineVideoUrl);

    if (!overlay || !playerHost || !videoId) {
      return;
    }

    let player = null;
    let playerReady = false;
    let shouldPlay = false;

    const showOverlay = () => overlay.classList.remove("is-hidden");
    const hideOverlay = () => overlay.classList.add("is-hidden");

    container.pauseInlineVideo = () => {
      shouldPlay = false;

      if (player && playerReady) {
        player.pauseVideo();
        player.seekTo(0, true);
      }

      showOverlay();
    };

    const createPlayer = () => {
      player = new window.YT.Player(playerHost, {
        videoId,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 0,
          controls: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: () => {
            playerReady = true;

            if (shouldPlay) {
              player.playVideo();
            }
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              hideOverlay();
              return;
            }

            if (
              event.data === window.YT.PlayerState.PAUSED ||
              event.data === window.YT.PlayerState.ENDED
            ) {
              shouldPlay = false;
              showOverlay();
            }
          },
        },
      });
    };

    const playVideo = () => {
      shouldPlay = true;

      this.loadYoutubeApi()
        .then(() => {
          if (!player) {
            createPlayer();
            return;
          }

          if (!playerReady) {
            return;
          }

          player.playVideo();
        })
        .catch(() => {
          shouldPlay = false;
        });
    };

    overlay.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        playVideo();
      },
      { signal: this.abortController.signal },
    );

    playerHost.addEventListener(
      "click",
      () => {
        if (!player || !playerReady) {
          playVideo();
        }
      },
      { signal: this.abortController.signal },
    );
  }

  pauseInlineVideos() {
    this.querySelectorAll(this.selectors.inlineVideo).forEach((container) => {
      if (typeof container.pauseInlineVideo === "function") {
        container.pauseInlineVideo();
        return;
      }

      const video = container.querySelector("video");
      const overlay = container.querySelector("[data-clc-inline-video-overlay]");

      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      overlay?.classList.remove("is-hidden");
    });
  }

  loadYoutubeApi() {
    if (window.YT?.Player) {
      return Promise.resolve();
    }

    if (this.youtubeApiPromise) {
      return this.youtubeApiPromise;
    }

    this.youtubeApiPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');

      if (existingScript) {
        const previousHandler = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof previousHandler === "function") {
            previousHandler();
          }
          resolve();
        };
        return;
      }

      window.onYouTubeIframeAPIReady = () => resolve();

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => reject(new Error("YouTube API failed to load"));
      document.head.appendChild(script);
    });

    return this.youtubeApiPromise;
  }

  bindModal() {
    const modal = this.querySelector(this.selectors.modal);
    const frame = this.querySelector(this.selectors.frame);
    const closeButton = this.querySelector("[data-clc-tabs-close]");

    if (!modal || !frame) {
      return;
    }

    closeButton?.addEventListener(
      "click",
      () => this.closeModal(modal, frame),
      { signal: this.abortController.signal },
    );

    modal.addEventListener(
      "click",
      (event) => {
        if (event.target === modal) {
          this.closeModal(modal, frame);
        }
      },
      { signal: this.abortController.signal },
    );

    modal.addEventListener(
      "close",
      () => {
        frame.src = "";
        this.unlockPageScroll();
      },
      { signal: this.abortController.signal },
    );
  }

  closeModal(modal, frame) {
    frame.src = "";

    if (typeof modal.close === "function") {
      modal.close();
    } else {
      modal.removeAttribute("open");
      this.unlockPageScroll();
    }
  }

  lockPageScroll() {
    this.lockedScrollY = window.scrollY;
    document.documentElement.classList.add("clc-pdp-mobile-tabs-modal-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.lockedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  unlockPageScroll() {
    if (!document.documentElement.classList.contains("clc-pdp-mobile-tabs-modal-open")) {
      return;
    }

    document.documentElement.classList.remove("clc-pdp-mobile-tabs-modal-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, this.lockedScrollY);
  }

  getYoutubeVideoId(url) {
    if (!url) {
      return "";
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);
      let videoId = "";

      if (parsedUrl.hostname.includes("youtu.be")) {
        videoId = parsedUrl.pathname.replace("/", "");
      } else if (parsedUrl.searchParams.has("v")) {
        videoId = parsedUrl.searchParams.get("v");
      } else if (parsedUrl.pathname.includes("/embed/")) {
        videoId = parsedUrl.pathname.split("/embed/").pop();
      } else if (parsedUrl.pathname.includes("/shorts/")) {
        videoId = parsedUrl.pathname.split("/shorts/").pop();
      }

      return videoId || "";
    } catch (error) {
      return "";
    }
  }

  getYoutubeEmbedUrl(url) {
    const videoId = this.getYoutubeVideoId(url);

    if (!videoId) {
      return "";
    }

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  }
}

if (!customElements.get("clc-pdp-mobile-tabs")) {
  customElements.define("clc-pdp-mobile-tabs", ClcPdpMobileTabs);
}
